import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";

import { useUser } from "@/api/hooks/useUser";
import { AccountType } from "@/api/rest/models/account";
import { logout } from "@/api/rest/services/authService";
import ItemsTable from "@/components/common/ItemsTable";
import { NotificationSettings } from "@/components/settings/NotificationSettings";
import ChangePasswordForm from "@/components/password-forms/ChangePasswordForm";
import ChangeEmailForm from "@/components/password-forms/ChangeEmailForm";
import { useDebugLifecycle } from "@/utils/debug/useDebugLifecycle";
import { useLanguage } from "@/utils/i18n/useLanguage";
import { isDobuEmployee } from "@/utils/userUtils";

export interface BannerState {
  message: string;
  type: "success" | "error";
}

export function AccountPage() {
  const account = useUser();

  const { t } = useTranslation();
  const { allLanguages, currentLanguageLabel, setLanguage } = useLanguage();

  // Local result banners moved to sub-forms

  useDebugLifecycle("AccountPage", {
    data: {
      url: window.location.href,
    },
  });

  const [isOpen, setIsOpen] = useState<false | number>(false);

  return (
    <main className="flex flex-grow flex-col md:p-12 md:overflow-y-auto scrollbar-thin scrollbar-track-slate-100 scrollbar-thumb-white lg:gap-4">
      <ExpandableSection
        title={t("account.language")}
        isOpen={isOpen === 0}
        highlightText={currentLanguageLabel}
        setIsOpen={() => setIsOpen(0)}
      >
        <div className="flex flex-row !bg-gray-300 rounded-lg overflow-clip">
          {allLanguages.map((language) => (
            <button
              key={language.key}
              className={clsx(
                "flex-grow text-gray-400 text-sm px-4 py-1  !bg-gray-300",
                language.value === currentLanguageLabel && "!bg-dobu-orange !text-white",
              )}
              onClick={() => setLanguage(language.key)}
            >
              {language.value}
            </button>
          ))}
        </div>
      </ExpandableSection>
      <ExpandableSection
        title={t("account.notificationPreferences")}
        isOpen={isOpen === 1}
        setIsOpen={() => setIsOpen(1)}
      >
        <NotificationSettings />
      </ExpandableSection>
      <ExpandableSection title={t("account.changePassword")} isOpen={isOpen === 2} setIsOpen={() => setIsOpen(2)}>
        <ChangePasswordForm className="flex flex-col gap-4 self-stretch" />
      </ExpandableSection>
      <ExpandableSection
        title={t("account.changeEmail")}
        highlightText={account?.email}
        isOpen={isOpen === 3}
        setIsOpen={() => setIsOpen(3)}
      >
        <ChangeEmailForm className="flex flex-col gap-4 self-stretch" />
      </ExpandableSection>
      <ExpandableSection
        title={t("account.myConnections")}
        highlightText={account?.name}
        isOpen={isOpen === 4}
        setIsOpen={() => setIsOpen(4)}
      >
        <ItemsTable
          items={(account?.accounts ?? []).map((acc) => ({
            id: acc.id,
            primaryText: acc.name ?? "",
            secondaryText: acc.identifier,
            type: acc.type === AccountType.ORGANIZATION ? "building" : "user",
          }))}
          showAdd={false}
        />
      </ExpandableSection>
      {isDobuEmployee(account) && (
        <Link
          to="/backoffice"
          className="flex flex-row self-stretch border-b border-gray-200 lg:bg-white lg:shadow-md lg:rounded-lg p-4 font-semibold text-dobu-orange hover:bg-gray-50"
        >
          {t("account.backoffice")}
        </Link>
      )}
      <span className="flex-grow lg:hidden" />
      <ExpandableSection className="text-red-700" title={t("login.logout")} isOpen={false} setIsOpen={() => logout()} />
    </main>
  );
}

export function ExpandableSection({
  className,
  children,
  title,
  highlightText,
  isOpen,
  setIsOpen,
}: {
  className?: string;
  children?: React.ReactNode;
  title: string;
  highlightText?: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}) {
  return (
    <div
      className={clsx(
        "flex flex-col self-stretch border-b last:border-t border-gray-200 lg:bg-white lg:shadow-md lg:rounded-lg",
        className,
      )}
    >
      <div className="flex flex-row self-stretch p-4 cursor-pointer" onClick={() => setIsOpen(!isOpen)}>
        <span className="flex flex-grow font-semibold">{title}</span>
        <span className="text-gray-400 text-sm mr-4 lg:mr-0">{highlightText}</span>
        <ChevronDownIcon
          className={clsx("text-gray-400 w-5 mt-[1px] h-5 lg:hidden", isOpen && "rotate-180", !children && "hidden")}
        />
      </div>
      <div className={clsx("px-4 pb-4 pt-2", children && "lg:!block", !isOpen && "hidden")}>{children}</div>
    </div>
  );
}
